import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { useQmrState } from '@packages/contexts/qmrs';
import { TextBadge } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';

const CVT_FAIL_CODES = ['TMA', 'TMB', 'MQJ'] as const;

/**
 * Checks a fail code against the CVT list. The badge shows in the picker before anything is saved, so it can't use
 * the report's CVT fields.
 */
function isCvtRelatedFailCode(failCode?: string): boolean {
    if (typeof failCode !== 'string') {
        return false;
    }

    const normalizedFailCode = failCode.trim().toUpperCase();

    return CVT_FAIL_CODES.some((code) => code === normalizedFailCode);
}

const useStyles = createUseStyles({
    inputBadge: {
        top: '50%',
        right: 48,
        position: 'absolute',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
    },
});

type CvtFailCodeRelatedBadgeProps = {
    failCode?: string;
    isInsideInput?: boolean;
};

/**
 * Renders a badge indicating that the fail code is related to CVT diagnostics.
 *
 * The badge is only rendered if the report is an NQR, the NQR CVT diagnostics
 * feature flag is enabled, and the fail code is one of `TMA`, `TMB`, or `MQJ`.
 *
 * @props The props for the component.
 * @returns {JSX.Element | null} The rendered badge for a CVT related fail code or null.
 */
export function CvtFailCodeRelatedBadge({ failCode, isInsideInput }: CvtFailCodeRelatedBadgeProps): JSX.Element | null {
    const classes = useStyles();
    const { isNqrCvtDiagnosticsFeatureEnabled } = useFeatureFlags();
    const { qmr } = useQmrState();
    const { t } = useTranslation();

    const isNqr = qmr?.isNqr === true;

    if (!isNqrCvtDiagnosticsFeatureEnabled || !isNqr || !isCvtRelatedFailCode(failCode)) {
        return null;
    }

    const badgeClassName = isInsideInput ? classes.inputBadge : undefined;

    return (
        <span className={badgeClassName}>
            <TextBadge
                variant="blue"
                style={styles.badge}
                textStyle={styles.text}
                typographyProps={{ numberOfLines: 1 }}
            >
                {t('qmr:sections.failCode.cvtDiagnostic.badge', 'CVT related')}
            </TextBadge>
        </span>
    );
}

const styles = StyleSheet.create({
    badge: {
        paddingVertical: 1,
        paddingHorizontal: 8,
        borderRadius: 4,
    },
    text: {
        fontSize: 13,
        fontWeight: '500',
        textTransform: 'none',
    },
});
